import { taskDetailPath } from '@/components/v2-production/layout/workspace-route-builders';
import {
  parseRelativePathname,
  workspaceDestinationFor,
} from '@/components/v2-production/layout/workspace-route-policy';
import { isNativePlatform } from '@/lib/mobile/platform';

const NATIVE_HOME = '/m/home';
const NATIVE_TASKS = '/m/tasks';

/**
 * The native screen a hosted workspace path maps to.
 *
 * INPUT reads both URL presentations: a stored `/v2/tasks/<id>` and a canonical `/tasks/<id>`
 * address the same task, and a native build can receive either from an old push row or a freshly
 * copied browser link.
 *
 * OUTPUT stays on the `/v2` + `/m/*` contract. A native build bundles its renderer, so an installed
 * binary can only route the paths it shipped knowing — widening what this EMITS would strand it.
 */
function getNativeTaskPath(path: string): string | null {
  // Identity comes from the RAW path component: `new URL()` resolves dot segments while parsing, so
  // reading `url.pathname` would let `/u/../tasks` arrive looking like a task path.
  const parts = parseRelativePathname(path);
  const destination = parts ? workspaceDestinationFor(parts.pathname) : undefined;
  if (!destination) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(path, 'https://placeholder.local');
  } catch {
    return null;
  }
  const { hash, search, searchParams } = url;
  if (destination.family === 'tasks') {
    const taskId = searchParams.get('taskId') ?? searchParams.get('task');
    return (taskId ? taskDetailPath(taskId, 'compatibility') : null) ?? NATIVE_TASKS;
  }
  if (destination.family === 'task-detail') {
    // Unrenderable identifiers are already refused by the parser, so this null is defensive, but
    // interpolating it would navigate the shell to the literal string "null?view=full".
    const detail = taskDetailPath(destination.taskId, 'compatibility');
    return detail === null ? NATIVE_TASKS : `${detail}${search}${hash}`;
  }
  if (destination.family === 'notifications') {
    return `/m/notifications${search}${hash}`;
  }
  return null;
}

export function mapPathForNative(path: string): string {
  if (!isNativePlatform()) {
    return path;
  }
  if (path === NATIVE_HOME || path.startsWith('/m/')) {
    return path;
  }
  return getNativeTaskPath(path) ?? NATIVE_HOME;
}
